import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../core/translate.pipe';
import { DsButtonDirective, DsCardDirective, DsInputDirective } from '../ui';
import { LocalizationService } from '../core/localization.service';
import {
  PlatformApi,
  type AdminItem,
  type AdministrationDashboard,
  type AuditEntry,
  type CurrentUser,
  type OperationJob,
  type PrivacyRequestRecord,
} from '../core/platform-api';

@Component({
  selector: 'app-admin-page',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DsButtonDirective,
    DsCardDirective,
    DsInputDirective,
  ],
  templateUrl: './admin-page.html',
  styleUrl: './admin-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPage {
  private readonly api = inject(PlatformApi);
  private readonly i18n = inject(LocalizationService);
  protected readonly user = signal<CurrentUser | null>(null);
  protected readonly dashboard = signal<AdministrationDashboard | null>(null);
  protected readonly items = signal<AdminItem[]>([]);
  protected readonly privacyRequests = signal<PrivacyRequestRecord[]>([]);
  protected readonly jobs = signal<OperationJob[]>([]);
  protected readonly audit = signal<AuditEntry[]>([]);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly email = new FormControl('demo.owner@example.test', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });

  protected async login(): Promise<void> {
    if (this.email.invalid) return;
    await this.run(async () => {
      this.user.set(await this.api.developmentLogin(this.email.value));
      await this.refresh();
    });
  }

  protected async importItem(): Promise<void> {
    await this.run(async () => {
      await this.api.importAdminDemoItem();
      await this.refresh();
    });
  }

  protected async publish(item: AdminItem): Promise<void> {
    await this.run(async () => {
      await this.api.publishAdminItem(item.id);
      await this.refresh();
    });
  }

  protected async process(request: PrivacyRequestRecord): Promise<void> {
    await this.run(async () => {
      await this.api.processAdminPrivacyRequest(request.id);
      await this.refresh();
    });
  }

  protected async cleanup(): Promise<void> {
    await this.run(async () => {
      await this.api.runRetentionCleanup();
      await this.refresh();
    });
  }

  private async refresh(): Promise<void> {
    const [dashboard, items, privacy, jobs, audit] = await Promise.all([
      this.api.getAdministrationDashboard(),
      this.api.listAdminItems(),
      this.api.listAdminPrivacyRequests(),
      this.api.listOperationJobs(),
      this.api.listAuditEntries(),
    ]);
    this.dashboard.set(dashboard);
    this.items.set(items);
    this.privacyRequests.set(privacy);
    this.jobs.set(jobs);
    this.audit.set(audit);
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    try {
      await action();
    } catch {
      this.error.set(this.i18n.t('admin.error'));
    } finally {
      this.busy.set(false);
    }
  }
}
